import type { User } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
  type Timestamp,
  type Unsubscribe
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import type { AssetInspection, ChecklistAnswer, ConservationState, InspectionContext } from "../types/imobi";
import { db, storage } from "./firebase";
import { enqueueOfflineChange } from "./localDb";

export type OfflineInspectionPhotoPayload = {
  companyId: string;
  assetId: string;
  inspectionId: string;
  questionId: string;
  file: File;
};

export function generateInspectionId(companyId: string, assetId: string): string {
  if (!db) {
    return `local-${Date.now()}`;
  }

  return doc(collection(db, "companies", companyId, "assets", assetId, "inspections")).id;
}

export async function queueOfflineInspectionPhoto(payload: OfflineInspectionPhotoPayload): Promise<void> {
  await enqueueOfflineChange({
    id: `inspectionPhoto-${payload.inspectionId}-${payload.questionId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    entity: "inspectionPhoto",
    action: "create",
    payload,
    createdAt: new Date().toISOString(),
    attempts: 0
  });
}

export async function attachInspectionPhoto(companyId: string, assetId: string, inspectionId: string, questionId: string, url: string, user: User): Promise<void> {
  if (!db) {
    return;
  }

  const inspectionRef = doc(db, "companies", companyId, "assets", assetId, "inspections", inspectionId);
  const snapshot = await getDoc(inspectionRef);
  if (!snapshot.exists()) {
    return;
  }

  const answers = (snapshot.data().answers as ChecklistAnswer[] | undefined) ?? [];
  const nextAnswers = answers.map((answer) => answer.questionId === questionId
    ? { ...answer, value: answer.type === "foto" ? "anexado" : answer.value, photoUrls: [...(answer.photoUrls ?? []), url] }
    : answer);

  await updateDoc(inspectionRef, {
    answers: nextAnswers,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function uploadInspectionPhotos(companyId: string, assetId: string, files: File[]): Promise<string[]> {
  if (!storage || !files.length) {
    return [];
  }

  const activeStorage = storage;
  const uploads = files.map(async (file, index) => {
    const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const filePath = `companies/${companyId}/assets/${assetId}/${Date.now()}-inspecao-${index}.${extension}`;
    const fileRef = ref(activeStorage, filePath);
    await uploadBytes(fileRef, file, {
      contentType: file.type || "image/jpeg",
      customMetadata: { companyId, assetId }
    });
    return getDownloadURL(fileRef);
  });

  return Promise.all(uploads);
}

export type CreateAssetInspectionInput = {
  id?: string;
  companyId: string;
  assetId: string;
  context: InspectionContext;
  externalServiceId?: string;
  externalServiceItemId?: string;
  checklistTemplateIds?: string[];
  checklistTemplateNames?: string[];
  answers: ChecklistAnswer[];
  conditionAssessed?: ConservationState;
  hasNewDamage?: boolean;
  notes?: string;
};

function serializeTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().toISOString();
  }

  return typeof value === "string" ? value : "";
}

function userName(user: User) {
  return user.displayName ?? user.email ?? user.uid;
}

function mapInspection(companyId: string, assetId: string, id: string, data: Record<string, unknown>): AssetInspection {
  return {
    id,
    companyId,
    assetId,
    context: (data.context ?? "retirada") as InspectionContext,
    externalServiceId: data.externalServiceId ? String(data.externalServiceId) : undefined,
    externalServiceItemId: data.externalServiceItemId ? String(data.externalServiceItemId) : undefined,
    checklistTemplateIds: Array.isArray(data.checklistTemplateIds)
      ? data.checklistTemplateIds.map(String)
      : (data.checklistTemplateId ? [String(data.checklistTemplateId)] : []),
    checklistTemplateNames: Array.isArray(data.checklistTemplateNames)
      ? data.checklistTemplateNames.map(String)
      : (data.checklistTemplateName ? [String(data.checklistTemplateName)] : []),
    answers: Array.isArray(data.answers) ? (data.answers as ChecklistAnswer[]) : [],
    conditionAssessed: data.conditionAssessed ? (data.conditionAssessed as ConservationState) : undefined,
    hasNewDamage: data.hasNewDamage === true,
    notes: data.notes ? String(data.notes) : undefined,
    performedByUserName: String(data.performedByUserName ?? ""),
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? "")
  };
}

export function subscribeAssetInspections(companyId: string | undefined, assetId: string | undefined, onChange: (inspections: AssetInspection[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId || !assetId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "assets", assetId, "inspections"), orderBy("createdAt", "desc")),
    (snapshot) => onChange(snapshot.docs.map((inspectionDoc) => mapInspection(companyId, assetId, inspectionDoc.id, inspectionDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function createAssetInspection(input: CreateAssetInspectionInput, user: User): Promise<string> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  const batch = writeBatch(db);
  const inspectionRef = input.id
    ? doc(db, "companies", input.companyId, "assets", input.assetId, "inspections", input.id)
    : doc(collection(db, "companies", input.companyId, "assets", input.assetId, "inspections"));
  const now = serverTimestamp();

  batch.set(inspectionRef, {
    context: input.context,
    externalServiceId: input.externalServiceId ?? null,
    externalServiceItemId: input.externalServiceItemId ?? null,
    checklistTemplateIds: input.checklistTemplateIds ?? [],
    checklistTemplateNames: input.checklistTemplateNames ?? [],
    answers: input.answers,
    conditionAssessed: input.conditionAssessed ?? null,
    hasNewDamage: input.hasNewDamage === true,
    notes: input.notes?.trim() || null,
    performedByUserName: userName(user),
    createdAt: now,
    createdBy: user.uid
  });

  if (input.externalServiceId) {
    const eventRef = doc(collection(db, "companies", input.companyId, "externalServices", input.externalServiceId, "events"));
    const contextLabel = input.context === "retirada" ? "retirada" : input.context === "devolucao" ? "devolução" : input.context === "transferencia" ? "transferência" : "auditoria";
    batch.set(eventRef, {
      type: "servico_atualizado",
      userId: user.uid,
      userName: userName(user),
      description: `Inspeção de ${contextLabel} registrada${input.hasNewDamage ? " com avaria identificada" : ""}.`,
      assetId: input.assetId,
      participantUserId: null,
      createdAt: now
    });

    if (input.externalServiceItemId && input.conditionAssessed && (input.context === "retirada" || input.context === "devolucao")) {
      const itemRef = doc(db, "companies", input.companyId, "externalServices", input.externalServiceId, "items", input.externalServiceItemId);
      batch.update(itemRef, input.context === "retirada"
        ? { conditionBefore: input.conditionAssessed, updatedAt: now }
        : { conditionAfter: input.conditionAssessed, updatedAt: now });
    }
  }

  await batch.commit();
  return inspectionRef.id;
}
